function initRetailerLocator() {
  const config = window.retailerLocatorConfig || {};
  const { createApp, markRaw } = Vue;

  const app = createApp({
data() {
      return {
        loading: true,
        map: null,
        markerCluster: null,
        error_message: null,
        locations: [],
        searchText: '',
        searchCity: '',
        searchCountry: '',
        selectedCategory: '',
        hiddenCategories: (config.hiddenCategories || []).map(c => c.trim()).filter(Boolean),
        allowedCategories: (() => {
  const list = (config.allowedCategories || []).map(c => c.trim()).filter(Boolean);
  return list.length ? list : ['Retailers', 'Duty Free'];
})(),
        selectedCountry: '',
        selectedCity: '',
        page: 0,
        itemsPerPage: 10,
        infowindow: null,
        mapBounds: null,
        isSettingBounds: false,
        boundsTimer: null,
        lang: (document.documentElement.lang || 'en').slice(0, 2).toLowerCase(),
        translations: {
          en: { categories: 'Categories', country: 'Country', city: 'City', search: 'Search', reset: 'Reset', all: 'All', placeholder: 'Search...' },
          fr: { categories: 'Catégories', country: 'Pays', city: 'Ville', search: 'Recherche', reset: 'Réinitialiser', all: 'Tous', placeholder: 'Rechercher...' },
          de: { categories: 'Kategorien', country: 'Land', city: 'Stadt', search: 'Suche', reset: 'Zurücksetzen', all: 'Alle', placeholder: 'Suchen...' },
          it: { categories: 'Categorie', country: 'Paese', city: 'Città', search: 'Ricerca', reset: 'Reimposta', all: 'Tutti', placeholder: 'Cerca...' },
          es: { categories: 'Categorías', country: 'País', city: 'Ciudad', search: 'Búsqueda', reset: 'Restablecer', all: 'Todos', placeholder: 'Buscar...' },
        },
      };
    },

    mounted() {
      this.initMap();
      this.query_locations();
    },

watch: {
      // La catégorie filtre les markers de la carte (type de point de vente)
      selectedCategory() {
        this.refresh_cluster();
        this.map_recenter();
        this.page = 0;
      },

      // Pays / ville / recherche : recentrage uniquement
      geo_trigger() {
        this.map_recenter();
        this.page = 0;
      }
    },

  computed: {
      // Locations correspondant à la catégorie sélectionnée — source des markers affichés
      category_locations() {
        if (!this.locations || this.locations.length === 0) return [];
        if (this.selectedCategory === '') return this.locations;
        return this.locations.filter(location =>
          Array.isArray(location.categories) && location.categories.includes(this.selectedCategory)
        );
      },

      // Locations correspondant à tous les sélecteurs — sert au recentrage de la carte
      selector_locations() {
        const searchText = this.searchText.toLowerCase();
        return this.category_locations.filter(location => {
          return (
            (this.selectedCountry === '' || location.data.country === this.selectedCountry) &&
            (this.selectedCity === '' || location.data.city === this.selectedCity) &&
            (searchText === '' || JSON.stringify(location.data).toLowerCase().includes(searchText))
          );
        });
      },

      // Déclencheur de recentrage pour les filtres géographiques (hors catégorie)
      geo_trigger() {
        return [this.selectedCountry, this.selectedCity, this.searchText].join('|');
      },

      hasActiveFilters() {
        return !!(this.selectedCountry || this.selectedCity || this.selectedCategory || this.searchText || this.searchCountry || this.searchCity);
      },

      // Ce qui s'affiche dans la liste : catégorie + visible sur la carte
      visible_locations() {
        if (this.mapBounds == null) return this.category_locations;
        return this.category_locations.filter(location => {
          if (!location.data.lat || !location.data.lng) return false;
          return this.mapBounds.contains(new google.maps.LatLng(location.data.lat, location.data.lng));
        });
      },

      fixedItemsPerPage() {
        return this.itemsPerPage;
      },

      numPages() {
        const n = Math.ceil(this.visible_locations.length / this.fixedItemsPerPage);
        if (this.page >= n) this.page = Math.max(0, n - 1);
        return n;
      },

      filtered_locations_paginated() {
        return this.paginate();
      },

      categories() {
        const unique = [''];
        this.locations.forEach(location => {
          if (Array.isArray(location.categories)) {
            location.categories.forEach(cat => {
              if (!unique.includes(cat)) unique.push(cat);
            });
          }
        });
        return unique.filter(c =>
  c !== '' && c !== 'FC Employee' && this.allowedCategories.includes(c)
).sort();
      },

      countries() {
        const vueApp = this;
        if (!this.locations) return [];

        let found_country = false;
        const filtered = this.locations.filter(location => {
          if (!Array.isArray(location.categories) || !location.categories.length) return false;
          if (this.selectedCategory !== '' && !location.categories.includes(this.selectedCategory)) return false;
          if (location.data.country && location.data.country.toLowerCase() === vueApp.selectedCountry.toLowerCase()) found_country = true;
          if (!this.searchCountry || !this.searchCountry.trim()) {
            return location.data.country && location.data.country.trim() !== '';
          }
          return (
            (location.data.country && location.data.country.toLowerCase().includes(this.searchCountry.toLowerCase())) ||
            (location.data.country_name && location.data.country_name.toLowerCase().includes(this.searchCountry.toLowerCase()))
          );
        });

        if (vueApp.selectedCountry !== '' && filtered.length && !found_country) {
          vueApp.selectedCountry = '';
        }

        const unique = Array.from(
          new Set(filtered.map(l => JSON.stringify({
            code: l.data.country,
            name: l.data.country_name || l.data.country || 'Unknown'
          })))
        ).map(s => JSON.parse(s));

        return unique.sort((a, b) => {
          const A = (a.name || '').toUpperCase();
          const B = (b.name || '').toUpperCase();
          return A < B ? -1 : A > B ? 1 : 0;
        });
      },

      cities() {
        const vueApp = this;
        if (!this.locations) return [];

        let found_city = false;
        const filtered = this.locations.filter(location => {
          if (!Array.isArray(location.categories) || !location.categories.length) return false;
          if (this.selectedCategory !== '' && !location.categories.includes(this.selectedCategory)) return false;
          if (location.data.city && vueApp.selectedCity && location.data.city.toLowerCase() === vueApp.selectedCity.toLowerCase()) found_city = true;
          return (
            (this.searchCity === '' || (location.data.city && location.data.city.toLowerCase().includes(this.searchCity.toLowerCase()))) &&
            (this.selectedCountry === '' || (location.data.country && location.data.country.toLowerCase() === this.selectedCountry.toLowerCase()))
          );
        });

        if (vueApp.selectedCity !== '' && (!filtered.length || !found_city)) {
          vueApp.selectedCity = '';
        }

        const unique = Array.from(
          new Set(filtered.map(l => JSON.stringify({ code: l.data.city, name: l.data.city })))
        ).map(s => JSON.parse(s));

        return unique.sort((a, b) => {
          const A = (a.name || '').toUpperCase();
          const B = (b.name || '').toUpperCase();
          return A < B ? -1 : A > B ? 1 : 0;
        });
      },

      t() {
        return this.translations[this.lang] || this.translations.en;
      },
    },

    methods: {
initMap() {
        this.map = markRaw(new google.maps.Map(this.$refs.map, {
          zoom: 2,
          center: { lat: 20, lng: 0 },
          mapTypeControlOptions: {
            mapTypeIds: ['roadmap', 'satellite', 'hybrid', 'terrain', 'styled_map'],
          },
        }));
        this.map.mapTypes.set('styled_map', this.get_map_style());
        this.map.setMapTypeId('styled_map');

        this.map.addListener('idle', () => {
          if (this.isSettingBounds) return;
          clearTimeout(this.boundsTimer);
          this.boundsTimer = setTimeout(() => {
            this.mapBounds = this.map.getBounds();
          }, 200);
        });
      },

      setPage(page) {
        this.page = page - 1;
        this.$refs.location_cards.scrollTo({ top: 0 });
        this.$refs.location_cards.scrollIntoView({ behavior: 'smooth', block: 'start' });
      },

   paginate() {
        const start = this.page * this.fixedItemsPerPage;
        return this.visible_locations.slice(start, start + this.fixedItemsPerPage);
      },

      clean_url(url) {
        return url ? url.split(/[?&#]/)[0] : '';
      },

map_recenter() {
        const bounds = new google.maps.LatLngBounds();
        this.selector_locations.forEach(location => {
          if (location.data.lat && location.data.lng) {
            bounds.extend(new google.maps.LatLng(location.data.lat, location.data.lng));
          }
        });
        if (!bounds.isEmpty()) {
          this.isSettingBounds = true;
          this.map.fitBounds(bounds);
          google.maps.event.addListenerOnce(this.map, 'idle', () => {
            this.mapBounds = this.map.getBounds();
            this.isSettingBounds = false;
          });
        }
      },

      set_locations_markers(locations) {
        locations.forEach(location => {
          if (!location.marker) this.set_location_marker(location);
        });
      },

      set_location_marker(location) {
        const data = location.data;
        if (!data || !data.lat || !data.lng) return;

        const markerOptions = {
          position: { lat: parseFloat(data.lat), lng: parseFloat(data.lng) },
          title: data.name,
        };

        if (config.markerIconUrl) {
          markerOptions.icon = {
            url: config.markerIconUrl,
            scaledSize: new google.maps.Size(32, 32)
          };
        }

        const marker = markRaw(new google.maps.Marker(markerOptions));
        location.marker = marker;
        marker.location_data = data;
        marker.location_place_id = location.place_id;

        const app = this;
        google.maps.event.addListener(marker, 'click', function () {
          if (app.infowindow) app.infowindow.close();
          app.infowindow = app.get_infowindow({ place_id: this.location_place_id, ...this.location_data });
          app.infowindow.open(app.map, this);
        });
      },

    generate_markerCluster(markers) {
        const algorithm = new markerClusterer.SuperClusterAlgorithm({});
        this.markerCluster = markRaw(new markerClusterer.MarkerClusterer({
          algorithm,
          map: this.map,
          markers,
          renderer: this,
        }));
      },

      render({ count, position }, stats) {
        const color = count > Math.max(10, stats.clusters.markers.mean) ? '#821815' : '#1f6d88';
        const size = count > 100 ? 55 : count > 10 ? 45 : 35;
        const svg = window.btoa(
          `<svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">` +
          `<circle cx="120" cy="120" opacity=".9" r="70"/>` +
          `<circle cx="120" cy="120" opacity=".4" r="90"/>` +
          `<circle cx="120" cy="120" opacity=".2" r="110"/>` +
          `</svg>`
        );
        return new google.maps.Marker({
          position,
          icon: { url: `data:image/svg+xml;base64,${svg}`, scaledSize: new google.maps.Size(size, size) },
          label: { text: String(count), color: 'rgba(255,255,255,0.9)', fontSize: '12px' },
          title: `Cluster of ${count} markers`,
          zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
        });
      },
get_infowindow(data) {
  let content = `
    <div style="font-family: inherit; font-size: 13px; min-width: 180px; max-width: 250px;">
      <b style="font-size: 14px;font-weight:bold!important;text-transform: uppercase;margin-bottom:10px;display:block;">${data.name || ''}</b><br>`;
  
  if (data.route) {
    content += data.route;
    if (data.street_number) content += `, ${data.street_number}`;
    content += '<br>';
  }
  if (data.zip_code || data.city) content += `${data.zip_code || ''} ${data.city || ''}<br>`;
  if (data.state && data.state !== data.city) content += `${data.state}<br>`;
  if (data.country_name) content += `${data.country_name}<br>`;
  content += '<br>';
  
  if (data.website && data.website.trim()) {
    const url = data.website.startsWith('http') ? data.website : 'https://' + data.website;
    content += `<a href="${url}" target="_blank" style="color:#000;text-decoration:none;margin-bottom:5px;">${data.website.split(/[?&#]/)[0]}</a><br>`;
  }
  if (data.phone && data.phone.trim()) content += `<a href="tel:${data.phone}" target="_blank" style="color:#000;text-decoration:none;margin-bottom:5px!important;display:block;">${data.phone}</a><br>`;
  if (data.email && data.email.trim()) content += `<a href="mailto:${data.email}" target="_blank" style="color:#000;text-decoration:none;">${data.email}</a><br>`;
  
  if (data.lat && data.lng) {
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${data.lat},${data.lng}`;
    content += `
      <br>
      <div style="display:flex; gap:12px; align-items:center;">
        <a href="${mapsUrl}" target="_blank" style="color:#000;text-decoration:none;">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 512 512" fill="currentColor" style="filter: drop-shadow(3px 3px 2px rgba(0,0,0,0.3));"><path d="M498.1 5.6c10.1 7 15.4 19.1 13.5 31.2l-64 416c-1.5 9.7-7.4 18.2-16 23s-18.9 5.4-28 1.6L284 427.7l-68.5 74.1c-8.9 9.7-22.9 12.9-35.2 8.1S160 493.2 160 480V396.4c0-4 1.5-7.8 4.2-10.7L331.8 202.8c5.8-6.3 5.6-16-.4-22s-15.7-6.4-22-.7L106 360.8 17.7 316.6C7.1 311.3 .3 300.7 0 288.9s6.1-22.8 16.1-28.7l448-256c10.7-6.1 23.9-5.5 34 1.4z"/></svg>
        </a>
      </div>`;
  }
  
  content += `</div>`;
  return new google.maps.InfoWindow({ content });
},

      zoom_location(event, location) {
        if (!location.marker) return;
        if (this.map) {
          this.map.panTo(location.marker.getPosition());
          this.map.setZoom(15);
        }
        if (this.infowindow) this.infowindow.close();
        this.infowindow = this.get_infowindow({ place_id: location.place_id, ...location.data });
        this.infowindow.open(this.map, location.marker);
        this.$refs.map.scrollIntoView({ behavior: 'smooth' });
      },

      onChangeCountry() {
        this.selectedCity = '';
      },
      onChangeCity() {},
      onChangeCategory() {},

   async query_locations() {
        try {
          const res = await fetch(config.apiUrl);
          if (!res.ok) throw new Error('Network error');
          let locations = await res.json();

          locations = locations.filter(l => !l.exclude);
          locations = locations.filter(l =>
  Array.isArray(l.categories) &&
  l.categories.some(c => this.allowedCategories.includes(c))
          );

          if (this.hiddenCategories.length > 0) {
            locations = locations.filter(l =>
              !this.hiddenCategories.some(hidden =>
                Array.isArray(l.categories) && l.categories.includes(hidden)
              )
            );
          }

          this.set_locations_markers(locations);
          this.locations = markRaw(locations);

          const markers = locations.map(l => l.marker).filter(Boolean);
          this.generate_markerCluster(markers);
        } catch (e) {
          this.error_message = 'There seems to be an error. Please try again later.';
        } finally {
          this.loading = false;
        }
      },

      get_map_style() {
        return new google.maps.StyledMapType([
          { featureType: 'administrative', elementType: 'labels.text.fill', stylers: [{ color: '#000000' }, { visibility: 'on' }, { weight: '0.01' }] },
          { featureType: 'administrative', elementType: 'labels.text.stroke', stylers: [{ visibility: 'off' }] },
          { featureType: 'landscape', elementType: 'all', stylers: [{ visibility: 'on' }, { color: '#c0c0c0' }] },
          { featureType: 'poi', elementType: 'all', stylers: [{ visibility: 'off' }] },
          { featureType: 'road', elementType: 'all', stylers: [{ saturation: -100 }, { lightness: '-11' }, { color: '#9b9b9b' }] },
          { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ weight: '0.35' }, { visibility: 'on' }, { color: '#9b9b9b' }] },
          { featureType: 'road', elementType: 'labels.text', stylers: [{ visibility: 'simplified' }, { color: '#6f6f6f' }] },
          { featureType: 'road.highway', elementType: 'all', stylers: [{ visibility: 'simplified' }, { lightness: '-7' }, { color: '#9b9b9b' }] },
          { featureType: 'road.arterial', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
          { featureType: 'transit', elementType: 'all', stylers: [{ visibility: 'off' }, { lightness: '-5' }, { color: '#ffffff' }] },
          { featureType: 'water', elementType: 'all', stylers: [{ color: '#ffffff' }, { visibility: 'on' }] }
        ]);
      },


      reset() {
        this.selectedCountry = '';
        this.selectedCity = '';
        this.selectedCategory = '';
        this.searchText = '';
        this.searchCountry = '';
        this.searchCity = '';
        this.page = 0;

        this.isSettingBounds = true;
        this.map.setZoom(2);
        this.map.setCenter({ lat: 20, lng: 0 });
        google.maps.event.addListenerOnce(this.map, 'idle', () => {
          this.mapBounds = this.map.getBounds();
          this.isSettingBounds = false;
        });
      },

      refresh_cluster() {
        if (!this.markerCluster) return;
        const markers = this.category_locations.map(l => l.marker).filter(Boolean);
        this.markerCluster.clearMarkers();
        this.markerCluster.addMarkers(markers);
      },
    }
  });

  window.storeLocatorApp = app.mount('#store-locator');
}